/* Find Your Ironclad: pure decision logic. No DOM, no vendors, UMD so node can test it. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.ICQCore = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* Section 1 routing table. Values are the exact strings the live Klaviyo flow splits on. */
  var ROUTES = {
    Gas:        { yes: 'P',  no: 'G'  },
    Induction:  { yes: 'P',  no: 'GI' },
    Campfire:   { yes: 'PF', no: 'G'  },
    'Pizza oven': { yes: 'PF', no: 'G' },
    Gift:       { yes: 'PG', no: 'GG' }
  };

  var ROUTE_KEYS = ['P', 'PF', 'PG', 'G', 'GI', 'GG'];

  /* Q1/Q2 labels -> values, self and gift variants (spec section 2). */
  var ANSWERS = {
    q1: [
      { label: 'Gas stove', value: 'Gas', icon: 'flame' },
      { label: 'Induction stove', value: 'Induction', icon: 'rings' },
      { label: 'Camping', value: 'Campfire', icon: 'tent' },
      { label: 'Pizza oven', value: 'Pizza oven', icon: 'dome' },
      { label: "It's a gift", value: 'Gift', icon: 'gift' }
    ],
    q2: {
      self: [
        { label: 'The best price possible', helper: 'Our lowest-priced way into Ironclad', value: 'yes' },
        { label: 'A pan to pass down to my grandkids', helper: 'Built for three generations of cooking', value: 'no' }
      ],
      gift: [
        { label: 'The best price possible', helper: "A gift that's easy to say yes to", value: 'yes' },
        { label: "One they'll pass down to their grandkids", helper: 'A gift for three generations', value: 'no' }
      ]
    }
  };

  /* CHANGE-05: screen-1 headline per ad angle (?a=). 'forever' is the default and is also what the
     liquid renders server-side. The same strings are duplicated in the inline script in
     sections/ironclad-quiz.liquid (it runs before first paint); keep them identical. */
  var ANGLES = {
    forever: {
      eyebrow: 'IRONCLAD · ABOUT 30 SECONDS',
      h1: 'Find your forever pan.',
      lede: "Two questions. Then we'll show you the pan we'd pick for you, with free shipping already applied."
    },
    nonstick: {
      eyebrow: 'FIND YOUR FOREVER PAN',
      h1: 'Done replacing non-stick?',
      lede: "Find the pan you'll keep. Two questions, then we'll show you the one we'd pick, with free shipping already applied."
    },
    generations: {
      eyebrow: 'FIND YOUR FOREVER PAN',
      h1: 'One pan. Three generations.',
      lede: "Two questions to find yours. We'll show you the pan we'd pick, with free shipping already applied."
    }
  };

  /* Key from ?a= (case-insensitive, trimmed) when it is a known angle, else 'forever'.
     Accepts location.search with or without the leading "?". */
  function angleFrom(search) {
    var pairs = String(search == null ? '' : search).replace(/^\?/, '').split('&');
    for (var i = 0; i < pairs.length; i++) {
      var kv = pairs[i].split('=');
      if (kv.length < 2) continue;
      var k;
      try { k = decodeURIComponent(kv[0].replace(/\+/g, '%20')); } catch (e) { k = kv[0]; }
      if (k !== 'a') continue;
      var v;
      try { v = decodeURIComponent(kv.slice(1).join('=').replace(/\+/g, '%20')); } catch (e) { v = kv.slice(1).join('='); }
      v = v.trim().toLowerCase();
      if (Object.prototype.hasOwnProperty.call(ANGLES, v)) return v;
      return 'forever';
    }
    return 'forever';
  }

  function routeFor(usingIronclad, priceSensitive) {
    var row = ROUTES[usingIronclad];
    if (!row) return null;
    var key = priceSensitive === 'yes' ? 'yes' : (priceSensitive === 'no' ? 'no' : null);
    if (!key) return null;
    return row[key];
  }

  /* Redirect through the discount URL so WELCOME lands in the session before the result renders. */
  /* Result page = the real PDP on the quiz-pick alternate template. The code is applied separately
     (discountPath) because browsers lose the query string through /discount/CODE?redirect=... */
  function resultUrl(code, pickUrl, route) {
    return String(pickUrl) + '?view=quiz-pick&r=' + encodeURIComponent(route);
  }
  function discountPath(code) {
    return '/discount/' + encodeURIComponent(code || 'WELCOME');
  }

  /* Whole dollars drop ".00", fractions keep 2 decimals, thousands comma. */
  function fmtMoney(amountMajor) {
    var n = Number(amountMajor) || 0;
    var whole = Math.abs(n % 1) < 0.005;
    var s = n.toLocaleString('en-NZ', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2
    });
    return '$' + s;
  }

  /* pure: price over the 100-year guarantee. enamel: over 25 years. Rounded to 2 decimals. */
  function perYear(priceMajor, family) {
    var years = family === 'enamel' ? 25 : 100;
    return Math.round(((Number(priceMajor) || 0) / years) * 100) / 100;
  }

  /* NZ $15 ($20 over threshold), AU $20 ($25 over). Values come from section settings. */
  function shipWorth(market, totalMajor, settings) {
    var s = settings || {};
    var over = (Number(totalMajor) || 0) >= (Number(s.ship_over_threshold) || 500);
    if (market === 'au') return over ? (s.ship_worth_au_over || 25) : (s.ship_worth_au || 20);
    return over ? (s.ship_worth_nz_over || 20) : (s.ship_worth_nz || 15);
  }

  /* Filter by the pick's clip_tags (any match), route-boost, cap 10, keep block order. */
  /* boostTags (optional, comma string or array): owner-set order from the pick block's clip_boost,
     e.g. "20cm,outdoor". Induction cooks (GI, or P with Induction) always see induction clips first. */
  function orderClips(clips, clipTags, route, usingIronclad, boostTags) {
    if (!Array.isArray(clips) || !clips.length) return [];
    var wanted = String(clipTags || '').split(',').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
    var matched = clips.filter(function (c) {
      var tags = String(c.tags || '').split(',').map(function (t) { return t.trim().toLowerCase(); });
      return wanted.some(function (w) { return tags.indexOf(w) !== -1; });
    });
    var boosts = [];
    if (route === 'GI' || (route === 'P' && usingIronclad === 'Induction')) boosts.push('induction');
    (Array.isArray(boostTags) ? boostTags : String(boostTags || '').split(','))
      .map(function (t) { return String(t).trim().toLowerCase(); })
      .filter(Boolean)
      .forEach(function (t) { if (boosts.indexOf(t) === -1) boosts.push(t); });
    if (boosts.length) {
      var rank = function (c) {
        for (var i = 0; i < boosts.length; i++) if (hasTag(c, boosts[i])) return i;
        return boosts.length;
      };
      matched = matched.map(function (c, i) { return { c: c, i: i, r: rank(c) }; })
        .sort(function (a, b) { return (a.r - b.r) || (a.i - b.i); }) /* ties keep block order */
        .map(function (x) { return x.c; });
    }
    return matched.slice(0, 10);
  }
  function hasTag(clip, tag) {
    return String(clip.tags || '').split(',').some(function (t) { return t.trim().toLowerCase() === tag; });
  }

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  function isValidEmail(s) {
    return typeof s === 'string' && EMAIL_RE.test(s.trim());
  }

  /* CHANGE-02 section 1: matched reviews under Kate's note. Priority order, no duplicates,
     capped at max (default 2):
       1. routes contains the route AND using contains the Q1 answer
       2. routes contains the route AND using is blank
       3. family equals the selected product's family (or "all")
     With no route (direct visit), priority 3 only. */
  /* Reviews shown under Kate's note are ONLY for the product on screen (the pick, or the upgrade when
     it's selected). review.handles = the product handles a review belongs to (colour siblings count as
     the same pan). Within that product: a review tagged with the visitor's answer comes first, then
     reviews tagged for their route, then untagged ones, then ones tagged for other routes; ties keep
     block order. Returns [] when the product has no reviews: never borrow another product's. */
  function pickReviews(reviews, productHandle, route, using, max) {
    if (!Array.isArray(reviews) || !reviews.length || !productHandle) return [];
    var m = Math.max(1, Number(max) || 2);
    function csv(s) {
      return String(s || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean);
    }
    var u = String(using || '').trim();
    var scored = [];
    for (var i = 0; i < reviews.length; i++) {
      var r = reviews[i];
      if (csv(r.handles || r.product_handles).indexOf(productHandle) === -1) continue;
      var routes = csv(r.routes), uses = csv(r.using), score = 2;
      if (u && uses.indexOf(u) !== -1) score = 0;
      else if (route && routes.indexOf(route) !== -1) score = 1;
      else if (routes.length || uses.length) score = 3;
      scored.push({ r: r, s: score, i: i });
    }
    scored.sort(function (a, b) { return (a.s - b.s) || (a.i - b.i); });
    return scored.slice(0, m).map(function (x) { return x.r; });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* {name} -> escaped first name. No name: drop "{name}, " and capitalise the next word. */
  function noteFor(route, name, templates) {
    var tpl = templates && templates[String(route || '').toLowerCase()];
    if (!tpl) return '';
    var clean = String(name || '').trim();
    if (clean) return String(tpl).replace('{name}', escapeHtml(clean));
    var re = /^\{name\},\s*/;
    if (!re.test(tpl)) return String(tpl);
    return String(tpl).replace(re, '').replace(/^([a-z])/, function (m) { return m.toUpperCase(); });
  }

  /* Finish colour from a product title or option value. */
  var COLOURS = [{ re: /salt\s*stone/i, name: 'Salt Stone', hex: '#F4F1DE' },
                 { re: /olive\s*leaf/i, name: 'Olive Leaf', hex: '#4D543A' },
                 { re: /antique\s*black/i, name: 'Antique Black', hex: '#1E1D1D' }];
  function colourOf(text) {
    var s = String(text || '');
    for (var i = 0; i < COLOURS.length; i++) if (COLOURS[i].re.test(s)) return COLOURS[i].name;
    return null;
  }

  /* Klaviyo profile + event props. Empty utm keys are omitted entirely. */
  function buildLeadProps(state) {
    var s = state || {};
    var props = {
      UsingIronclad: s.usingIronclad,
      'price-sensitive': s.priceSensitive,
      market: s.market,
      source: 'site_quiz_fyi',
      quiz_route: s.route,
      quiz_pick_handle: s.pickHandle,
      quiz_completed_at: s.completedAt,
      quiz_angle: s.angle || 'forever'
    };
    var utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    var utms = s.utms || {};
    for (var i = 0; i < utmKeys.length; i++) {
      var v = utms[utmKeys[i]];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        props['quiz_' + utmKeys[i]] = v;
      }
    }
    return props;
  }

  return {
    ROUTES: ROUTES,
    ROUTE_KEYS: ROUTE_KEYS,
    ANSWERS: ANSWERS,
    ANGLES: ANGLES,
    angleFrom: angleFrom,
    routeFor: routeFor,
    resultUrl: resultUrl,
    discountPath: discountPath,
    fmtMoney: fmtMoney,
    perYear: perYear,
    shipWorth: shipWorth,
    orderClips: orderClips,
    isValidEmail: isValidEmail,
    pickReviews: pickReviews,
    escapeHtml: escapeHtml,
    noteFor: noteFor,
    colourOf: colourOf,
    buildLeadProps: buildLeadProps
  };
});
